import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Building2, CheckCircle2, Handshake, ArrowLeft, Mail, Phone, MapPin } from "lucide-react";

// Language configuration with country codes for flags
const languages = [
  { code: "en", name: "English", countryCode: "gb" },
  { code: "it", name: "Italiano", countryCode: "it" },
  { code: "es", name: "Español", countryCode: "es" },
  { code: "fr", name: "Français", countryCode: "fr" },
  { code: "pt", name: "Português", countryCode: "br" },
  { code: "vi", name: "Tiếng Việt", countryCode: "vn" },
  { code: "tl", name: "Tagalog", countryCode: "ph" },
  { code: "ms", name: "Bahasa Melayu", countryCode: "my" },
  { code: "tr", name: "Türkçe", countryCode: "tr" },
  { code: "ru", name: "Русский", countryCode: "ru" },
  { code: "zh", name: "中文", countryCode: "cn" },
  { code: "ja", name: "日本語", countryCode: "jp" },
  { code: "ko", name: "한국어", countryCode: "kr" },
];

const content = {
  en: {
    backToHome: "Back to Home",
    pageTitle: "Partner With Us",
    pageSubtitle: "Are you a rental shop or vehicle provider?",
    pageDescription: "We send you new customers — no commitment.",
    features: [
      "Receive pre-qualified rider leads",
      "No upfront costs",
      "Optional referral bonus program",
      "Direct communication channel"
    ],
    cta: "Become a Partner",
    statsTitle: "Rental Partners",
    statsSubtitle: "Grow your customer base",
    newLeadsPerMonth: "New leads per month",
    commissionFees: "Commission fees",
    setupTime: "Setup time",
    howItWorksTitle: "How Partnership Works",
    howItWorksSteps: [
      { title: "Register", description: "Fill out the partner form below" },
      { title: "Get Verified", description: "We verify your business details" },
      { title: "Receive Leads", description: "Start receiving rider referrals" },
      { title: "Grow Together", description: "Build a lasting partnership" }
    ],
    benefitsTitle: "Benefits of Partnering",
    benefits: [
      { title: "Zero Cost Entry", description: "No registration fees or monthly charges" },
      { title: "Quality Leads", description: "Pre-screened riders ready to rent" },
      { title: "Flexible Terms", description: "Set your own rental conditions" },
      { title: "Direct Support", description: "Dedicated partner support line" }
    ],
    contactTitle: "Interested in Partnering?",
    contactDescription: "Contact us to discuss partnership opportunities",
    contactEmail: "partners@justdelivery.com.au",
    contactPhone: "+61 XXX XXX XXX"
  },
  it: {
    backToHome: "Torna alla Home",
    pageTitle: "Diventa Partner",
    pageSubtitle: "Sei un negozio di noleggio o fornitore di veicoli?",
    pageDescription: "Ti inviamo nuovi clienti — senza impegno.",
    features: [
      "Ricevi lead pre-qualificati",
      "Nessun costo iniziale",
      "Programma bonus referral opzionale",
      "Canale di comunicazione diretto"
    ],
    cta: "Diventa Partner",
    statsTitle: "Partner Noleggio",
    statsSubtitle: "Fai crescere i tuoi clienti",
    newLeadsPerMonth: "Nuovi lead al mese",
    commissionFees: "Commissioni",
    setupTime: "Tempo di configurazione",
    howItWorksTitle: "Come Funziona la Partnership",
    howItWorksSteps: [
      { title: "Registrati", description: "Compila il modulo partner qui sotto" },
      { title: "Verifica", description: "Verifichiamo i dettagli della tua attività" },
      { title: "Ricevi Lead", description: "Inizia a ricevere referral di rider" },
      { title: "Cresci Insieme", description: "Costruisci una partnership duratura" }
    ],
    benefitsTitle: "Vantaggi della Partnership",
    benefits: [
      { title: "Ingresso Gratuito", description: "Nessuna quota di registrazione o costi mensili" },
      { title: "Lead di Qualità", description: "Rider pre-selezionati pronti a noleggiare" },
      { title: "Termini Flessibili", description: "Imposta le tue condizioni di noleggio" },
      { title: "Supporto Diretto", description: "Linea di supporto partner dedicata" }
    ],
    contactTitle: "Interessato a Diventare Partner?",
    contactDescription: "Contattaci per discutere opportunità di partnership",
    contactEmail: "partners@justdelivery.com.au",
    contactPhone: "+61 XXX XXX XXX"
  },
  es: {
    backToHome: "Volver al Inicio",
    pageTitle: "Asóciate con Nosotros",
    pageSubtitle: "¿Eres una tienda de alquiler o proveedor de vehículos?",
    pageDescription: "Te enviamos nuevos clientes — sin compromiso.",
    features: [
      "Recibe leads pre-calificados",
      "Sin costos iniciales",
      "Programa de bonos por referidos opcional",
      "Canal de comunicación directo"
    ],
    cta: "Convertirse en Socio",
    statsTitle: "Socios de Alquiler",
    statsSubtitle: "Haz crecer tu base de clientes",
    newLeadsPerMonth: "Nuevos leads por mes",
    commissionFees: "Comisiones",
    setupTime: "Tiempo de configuración",
    howItWorksTitle: "Cómo Funciona la Asociación",
    howItWorksSteps: [
      { title: "Regístrate", description: "Completa el formulario de socio" },
      { title: "Verificación", description: "Verificamos los detalles de tu negocio" },
      { title: "Recibe Leads", description: "Comienza a recibir referidos" },
      { title: "Crece Juntos", description: "Construye una asociación duradera" }
    ],
    benefitsTitle: "Beneficios de Asociarse",
    benefits: [
      { title: "Entrada Sin Costo", description: "Sin tarifas de registro ni cargos mensuales" },
      { title: "Leads de Calidad", description: "Repartidores pre-seleccionados listos para alquilar" },
      { title: "Términos Flexibles", description: "Establece tus propias condiciones de alquiler" },
      { title: "Soporte Directo", description: "Línea de soporte dedicada para socios" }
    ],
    contactTitle: "¿Interesado en Asociarte?",
    contactDescription: "Contáctanos para discutir oportunidades de asociación",
    contactEmail: "partners@justdelivery.com.au",
    contactPhone: "+61 XXX XXX XXX"
  },
  fr: {
    backToHome: "Retour à l'Accueil",
    pageTitle: "Devenez Partenaire",
    pageSubtitle: "Êtes-vous un magasin de location ou un fournisseur de véhicules?",
    pageDescription: "Nous vous envoyons de nouveaux clients — sans engagement.",
    features: [
      "Recevez des leads pré-qualifiés",
      "Aucun coût initial",
      "Programme de bonus de parrainage optionnel",
      "Canal de communication direct"
    ],
    cta: "Devenir Partenaire",
    statsTitle: "Partenaires de Location",
    statsSubtitle: "Développez votre clientèle",
    newLeadsPerMonth: "Nouveaux leads par mois",
    commissionFees: "Frais de commission",
    setupTime: "Temps de configuration",
    howItWorksTitle: "Comment Fonctionne le Partenariat",
    howItWorksSteps: [
      { title: "Inscrivez-vous", description: "Remplissez le formulaire partenaire" },
      { title: "Vérification", description: "Nous vérifions les détails de votre entreprise" },
      { title: "Recevez des Leads", description: "Commencez à recevoir des références" },
      { title: "Grandissez Ensemble", description: "Construisez un partenariat durable" }
    ],
    benefitsTitle: "Avantages du Partenariat",
    benefits: [
      { title: "Entrée Gratuite", description: "Pas de frais d'inscription ni de charges mensuelles" },
      { title: "Leads de Qualité", description: "Livreurs pré-sélectionnés prêts à louer" },
      { title: "Conditions Flexibles", description: "Définissez vos propres conditions de location" },
      { title: "Support Direct", description: "Ligne de support partenaire dédiée" }
    ],
    contactTitle: "Intéressé par le Partenariat?",
    contactDescription: "Contactez-nous pour discuter des opportunités de partenariat",
    contactEmail: "partners@justdelivery.com.au",
    contactPhone: "+61 XXX XXX XXX"
  },
  pt: {
    backToHome: "Voltar ao Início",
    pageTitle: "Torne-se Parceiro",
    pageSubtitle: "É uma loja de aluguer ou fornecedor de veículos?",
    pageDescription: "Enviamos-lhe novos clientes — sem compromisso.",
    features: [
      "Receba leads pré-qualificados",
      "Sem custos iniciais",
      "Programa de bónus de referência opcional",
      "Canal de comunicação direto"
    ],
    cta: "Tornar-se Parceiro",
    statsTitle: "Parceiros de Aluguer",
    statsSubtitle: "Faça crescer a sua base de clientes",
    newLeadsPerMonth: "Novos leads por mês",
    commissionFees: "Taxas de comissão",
    setupTime: "Tempo de configuração",
    howItWorksTitle: "Como Funciona a Parceria",
    howItWorksSteps: [
      { title: "Registe-se", description: "Preencha o formulário de parceiro" },
      { title: "Verificação", description: "Verificamos os detalhes do seu negócio" },
      { title: "Receba Leads", description: "Comece a receber referências" },
      { title: "Cresça Connosco", description: "Construa uma parceria duradoura" }
    ],
    benefitsTitle: "Benefícios da Parceria",
    benefits: [
      { title: "Entrada Gratuita", description: "Sem taxas de registo ou custos mensais" },
      { title: "Leads de Qualidade", description: "Entregadores pré-selecionados prontos para alugar" },
      { title: "Termos Flexíveis", description: "Defina as suas próprias condições de aluguer" },
      { title: "Suporte Direto", description: "Linha de suporte dedicada para parceiros" }
    ],
    contactTitle: "Interessado em Ser Parceiro?",
    contactDescription: "Contacte-nos para discutir oportunidades de parceria",
    contactEmail: "partners@justdelivery.com.au",
    contactPhone: "+61 XXX XXX XXX"
  },
  vi: {
    backToHome: "Quay lại Trang chủ",
    pageTitle: "Hợp tác với Chúng tôi",
    pageSubtitle: "Bạn là cửa hàng cho thuê hoặc nhà cung cấp xe?",
    pageDescription: "Chúng tôi gửi khách hàng mới đến bạn — không cam kết.",
    features: [
      "Nhận leads đã được sàng lọc",
      "Không chi phí ban đầu",
      "Chương trình thưởng giới thiệu tùy chọn",
      "Kênh liên lạc trực tiếp"
    ],
    cta: "Trở thành Đối tác",
    statsTitle: "Đối tác Cho thuê",
    statsSubtitle: "Phát triển cơ sở khách hàng",
    newLeadsPerMonth: "Leads mới mỗi tháng",
    commissionFees: "Phí hoa hồng",
    setupTime: "Thời gian thiết lập",
    howItWorksTitle: "Cách Hợp tác Hoạt động",
    howItWorksSteps: [
      { title: "Đăng ký", description: "Điền vào biểu mẫu đối tác" },
      { title: "Xác minh", description: "Chúng tôi xác minh thông tin doanh nghiệp" },
      { title: "Nhận Leads", description: "Bắt đầu nhận giới thiệu tài xế" },
      { title: "Cùng Phát triển", description: "Xây dựng quan hệ đối tác lâu dài" }
    ],
    benefitsTitle: "Lợi ích của Hợp tác",
    benefits: [
      { title: "Miễn phí Tham gia", description: "Không phí đăng ký hoặc phí hàng tháng" },
      { title: "Leads Chất lượng", description: "Tài xế đã sàng lọc sẵn sàng thuê" },
      { title: "Điều khoản Linh hoạt", description: "Đặt điều kiện cho thuê của riêng bạn" },
      { title: "Hỗ trợ Trực tiếp", description: "Đường dây hỗ trợ đối tác riêng" }
    ],
    contactTitle: "Quan tâm đến Hợp tác?",
    contactDescription: "Liên hệ để thảo luận cơ hội hợp tác",
    contactEmail: "partners@justdelivery.com.au",
    contactPhone: "+61 XXX XXX XXX"
  },
  tl: {
    backToHome: "Bumalik sa Home",
    pageTitle: "Makipagsosyo sa Amin",
    pageSubtitle: "Ikaw ba ay rental shop o vehicle provider?",
    pageDescription: "Nagpapadala kami ng bagong customers — walang commitment.",
    features: [
      "Tumanggap ng pre-qualified leads",
      "Walang upfront costs",
      "Optional referral bonus program",
      "Direct communication channel"
    ],
    cta: "Maging Partner",
    statsTitle: "Rental Partners",
    statsSubtitle: "Palakihin ang customer base",
    newLeadsPerMonth: "Bagong leads bawat buwan",
    commissionFees: "Commission fees",
    setupTime: "Setup time",
    howItWorksTitle: "Paano Gumagana ang Partnership",
    howItWorksSteps: [
      { title: "Mag-register", description: "Punan ang partner form" },
      { title: "Ma-verify", description: "I-verify namin ang business details" },
      { title: "Tumanggap ng Leads", description: "Magsimulang tumanggap ng referrals" },
      { title: "Lumago Kasama", description: "Bumuo ng lasting partnership" }
    ],
    benefitsTitle: "Mga Benepisyo ng Pagsasosyo",
    benefits: [
      { title: "Libreng Pasok", description: "Walang registration fees o monthly charges" },
      { title: "Quality Leads", description: "Pre-screened riders na handang mag-rent" },
      { title: "Flexible Terms", description: "Itakda ang sariling rental conditions" },
      { title: "Direct Support", description: "Dedicated partner support line" }
    ],
    contactTitle: "Interesado sa Partnership?",
    contactDescription: "Makipag-ugnayan para pag-usapan ang partnership",
    contactEmail: "partners@justdelivery.com.au",
    contactPhone: "+61 XXX XXX XXX"
  },
  ms: {
    backToHome: "Kembali ke Utama",
    pageTitle: "Rakan Kongsi Kami",
    pageSubtitle: "Adakah anda kedai sewa atau pembekal kenderaan?",
    pageDescription: "Kami hantar pelanggan baru kepada anda — tiada komitmen.",
    features: [
      "Terima leads yang telah disaring",
      "Tiada kos pendahuluan",
      "Program bonus rujukan pilihan",
      "Saluran komunikasi langsung"
    ],
    cta: "Jadi Rakan Kongsi",
    statsTitle: "Rakan Kongsi Sewa",
    statsSubtitle: "Kembangkan pangkalan pelanggan",
    newLeadsPerMonth: "Leads baru setiap bulan",
    commissionFees: "Yuran komisyen",
    setupTime: "Masa persediaan",
    howItWorksTitle: "Cara Perkongsian Berfungsi",
    howItWorksSteps: [
      { title: "Daftar", description: "Isi borang rakan kongsi" },
      { title: "Pengesahan", description: "Kami sahkan butiran perniagaan" },
      { title: "Terima Leads", description: "Mula terima rujukan" },
      { title: "Berkembang Bersama", description: "Bina perkongsian berpanjangan" }
    ],
    benefitsTitle: "Faedah Perkongsian",
    benefits: [
      { title: "Kemasukan Percuma", description: "Tiada yuran pendaftaran atau caj bulanan" },
      { title: "Leads Berkualiti", description: "Rider yang telah disaring sedia untuk menyewa" },
      { title: "Terma Fleksibel", description: "Tetapkan syarat sewaan sendiri" },
      { title: "Sokongan Langsung", description: "Talian sokongan rakan kongsi khusus" }
    ],
    contactTitle: "Berminat untuk Berkongsi?",
    contactDescription: "Hubungi kami untuk membincangkan peluang perkongsian",
    contactEmail: "partners@justdelivery.com.au",
    contactPhone: "+61 XXX XXX XXX"
  },
  tr: {
    backToHome: "Ana Sayfaya Dön",
    pageTitle: "Bizimle Ortak Olun",
    pageSubtitle: "Kiralama dükkanı veya araç tedarikçisi misiniz?",
    pageDescription: "Size yeni müşteriler gönderiyoruz — taahhüt yok.",
    features: [
      "Ön elemeyi geçmiş müşteri adayları alın",
      "Peşin maliyet yok",
      "Opsiyonel tavsiye bonus programı",
      "Doğrudan iletişim kanalı"
    ],
    cta: "Ortak Olun",
    statsTitle: "Kiralama Ortakları",
    statsSubtitle: "Müşteri tabanınızı büyütün",
    newLeadsPerMonth: "Aylık yeni müşteri adayı",
    commissionFees: "Komisyon ücretleri",
    setupTime: "Kurulum süresi",
    howItWorksTitle: "Ortaklık Nasıl Çalışır",
    howItWorksSteps: [
      { title: "Kayıt Olun", description: "Ortak formunu doldurun" },
      { title: "Doğrulama", description: "İşletme bilgilerinizi doğrularız" },
      { title: "Müşteri Adayı Alın", description: "Sürücü yönlendirmeleri almaya başlayın" },
      { title: "Birlikte Büyüyün", description: "Kalıcı bir ortaklık kurun" }
    ],
    benefitsTitle: "Ortaklığın Faydaları",
    benefits: [
      { title: "Ücretsiz Giriş", description: "Kayıt ücreti veya aylık ücret yok" },
      { title: "Kaliteli Müşteri Adayları", description: "Kiralamaya hazır ön elemeyi geçmiş sürücüler" },
      { title: "Esnek Şartlar", description: "Kendi kiralama koşullarınızı belirleyin" },
      { title: "Doğrudan Destek", description: "Özel ortak destek hattı" }
    ],
    contactTitle: "Ortaklıkla İlgileniyor musunuz?",
    contactDescription: "Ortaklık fırsatlarını görüşmek için bizimle iletişime geçin",
    contactEmail: "partners@justdelivery.com.au",
    contactPhone: "+61 XXX XXX XXX"
  },
  ru: {
    backToHome: "Вернуться на главную",
    pageTitle: "Станьте Партнером",
    pageSubtitle: "Вы арендный магазин или поставщик транспортных средств?",
    pageDescription: "Мы присылаем вам новых клиентов — без обязательств.",
    features: [
      "Получайте предварительно отобранных клиентов",
      "Без начальных затрат",
      "Опциональная реферальная программа",
      "Прямой канал связи"
    ],
    cta: "Стать Партнером",
    statsTitle: "Арендные Партнеры",
    statsSubtitle: "Расширяйте клиентскую базу",
    newLeadsPerMonth: "Новых клиентов в месяц",
    commissionFees: "Комиссионные сборы",
    setupTime: "Время настройки",
    howItWorksTitle: "Как Работает Партнерство",
    howItWorksSteps: [
      { title: "Регистрация", description: "Заполните партнерскую форму" },
      { title: "Проверка", description: "Мы проверяем данные вашего бизнеса" },
      { title: "Получайте Клиентов", description: "Начните получать рефералов" },
      { title: "Растите Вместе", description: "Постройте долгосрочное партнерство" }
    ],
    benefitsTitle: "Преимущества Партнерства",
    benefits: [
      { title: "Бесплатный Вход", description: "Без регистрационных сборов или ежемесячных платежей" },
      { title: "Качественные Клиенты", description: "Предварительно отобранные курьеры готовы арендовать" },
      { title: "Гибкие Условия", description: "Устанавливайте свои условия аренды" },
      { title: "Прямая Поддержка", description: "Выделенная линия поддержки партнеров" }
    ],
    contactTitle: "Заинтересованы в Партнерстве?",
    contactDescription: "Свяжитесь с нами для обсуждения возможностей партнерства",
    contactEmail: "partners@justdelivery.com.au",
    contactPhone: "+61 XXX XXX XXX"
  },
  zh: {
    backToHome: "返回首页",
    pageTitle: "与我们合作",
    pageSubtitle: "您是租赁店或车辆提供商吗？",
    pageDescription: "我们为您送来新客户——无需承诺。",
    features: [
      "获得预筛选骑手线索",
      "无前期费用",
      "可选推荐奖金计划",
      "直接沟通渠道"
    ],
    cta: "成为合作伙伴",
    statsTitle: "租赁合作伙伴",
    statsSubtitle: "扩大您的客户群",
    newLeadsPerMonth: "每月新线索",
    commissionFees: "佣金费用",
    setupTime: "设置时间",
    howItWorksTitle: "合作如何运作",
    howItWorksSteps: [
      { title: "注册", description: "填写合作伙伴表单" },
      { title: "验证", description: "我们验证您的业务详情" },
      { title: "获取线索", description: "开始接收骑手推荐" },
      { title: "共同成长", description: "建立长期合作关系" }
    ],
    benefitsTitle: "合作优势",
    benefits: [
      { title: "免费入门", description: "无注册费或月费" },
      { title: "优质线索", description: "预筛选的骑手准备租赁" },
      { title: "灵活条款", description: "设置您自己的租赁条件" },
      { title: "直接支持", description: "专属合作伙伴支持热线" }
    ],
    contactTitle: "有兴趣合作吗？",
    contactDescription: "联系我们讨论合作机会",
    contactEmail: "partners@justdelivery.com.au",
    contactPhone: "+61 XXX XXX XXX"
  },
  ja: {
    backToHome: "ホームに戻る",
    pageTitle: "パートナーになる",
    pageSubtitle: "レンタルショップまたは車両プロバイダーですか？",
    pageDescription: "新規顧客をお送りします — コミットメント不要。",
    features: [
      "事前審査済みライダーリードを取得",
      "初期費用なし",
      "オプションの紹介ボーナスプログラム",
      "直接コミュニケーションチャンネル"
    ],
    cta: "パートナーになる",
    statsTitle: "レンタルパートナー",
    statsSubtitle: "顧客基盤を拡大",
    newLeadsPerMonth: "月間新規リード",
    commissionFees: "手数料",
    setupTime: "セットアップ時間",
    howItWorksTitle: "パートナーシップの仕組み",
    howItWorksSteps: [
      { title: "登録", description: "パートナーフォームを記入" },
      { title: "確認", description: "ビジネス詳細を確認します" },
      { title: "リード獲得", description: "ライダー紹介の受け取り開始" },
      { title: "共に成長", description: "長期的なパートナーシップを構築" }
    ],
    benefitsTitle: "パートナーシップのメリット",
    benefits: [
      { title: "無料参加", description: "登録料や月額料金なし" },
      { title: "質の高いリード", description: "レンタル準備済みの事前審査済みライダー" },
      { title: "柔軟な条件", description: "独自のレンタル条件を設定" },
      { title: "直接サポート", description: "専用パートナーサポートライン" }
    ],
    contactTitle: "パートナーシップに興味がありますか？",
    contactDescription: "パートナーシップの機会についてご連絡ください",
    contactEmail: "partners@justdelivery.com.au",
    contactPhone: "+61 XXX XXX XXX"
  },
  ko: {
    backToHome: "홈으로 돌아가기",
    pageTitle: "파트너 되기",
    pageSubtitle: "렌탈샵 또는 차량 공급업체이신가요?",
    pageDescription: "새로운 고객을 보내드립니다 — 약정 없음.",
    features: [
      "사전 심사된 라이더 리드 받기",
      "선불 비용 없음",
      "선택적 추천 보너스 프로그램",
      "직접 커뮤니케이션 채널"
    ],
    cta: "파트너 되기",
    statsTitle: "렌탈 파트너",
    statsSubtitle: "고객 기반 확대",
    newLeadsPerMonth: "월간 신규 리드",
    commissionFees: "수수료",
    setupTime: "설정 시간",
    howItWorksTitle: "파트너십 작동 방식",
    howItWorksSteps: [
      { title: "등록", description: "파트너 양식 작성" },
      { title: "확인", description: "비즈니스 정보 확인" },
      { title: "리드 받기", description: "라이더 추천 받기 시작" },
      { title: "함께 성장", description: "장기적인 파트너십 구축" }
    ],
    benefitsTitle: "파트너십 혜택",
    benefits: [
      { title: "무료 가입", description: "등록비나 월 요금 없음" },
      { title: "양질의 리드", description: "렌탈 준비가 된 사전 심사 라이더" },
      { title: "유연한 조건", description: "자체 렌탈 조건 설정" },
      { title: "직접 지원", description: "전용 파트너 지원 라인" }
    ],
    contactTitle: "파트너십에 관심이 있으신가요?",
    contactDescription: "파트너십 기회를 논의하려면 연락해 주세요",
    contactEmail: "partners@justdelivery.com.au",
    contactPhone: "+61 XXX XXX XXX"
  }
};

export default function ForShops() {
  const [searchParams] = useSearchParams();
  const [lang, setLang] = useState("en");

  useEffect(() => {
    const urlLang = searchParams.get("lang");
    const storedLang = localStorage.getItem("justdelivery-lang");
    const browserLang = navigator.language.split("-")[0];
    
    const supportedLangs = languages.map(l => l.code);
    
    if (urlLang && supportedLangs.includes(urlLang)) {
      setLang(urlLang);
      localStorage.setItem("justdelivery-lang", urlLang);
    } else if (storedLang && supportedLangs.includes(storedLang)) {
      setLang(storedLang);
    } else if (supportedLangs.includes(browserLang)) {
      setLang(browserLang);
    }
  }, [searchParams]);

  const t = content[lang] || content.en;

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="bg-black border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to={`/?lang=${lang}`} className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
              <span>{t.backToHome}</span>
            </Link>
            <Link to={`/?lang=${lang}`} className="text-2xl font-black">
              <span className="text-[#6ABF4B]">just</span>
              <span className="text-[#E53935]">delivery</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-black to-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div 
              initial={{ opacity: 0, x: -30 }} 
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#E53935]/20 text-[#E53935] text-sm font-semibold rounded-full mb-6">
                <Building2 className="w-4 h-4" />
                Partner Program
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">{t.pageTitle}</h1>
              <p className="text-xl text-gray-400 mb-4">{t.pageSubtitle}</p>
              <p className="text-gray-400 mb-8">{t.pageDescription}</p>
              <ul className="space-y-3 mb-8">
                {t.features.map((f, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#E53935]" />
                    <span className="text-gray-300">{f}</span>
                  </li>
                ))}
              </ul>
              <a 
                href={`mailto:partners@justdelivery.com.au?subject=Partnership Inquiry`}
                className="inline-flex items-center gap-2 px-8 py-4 bg-[#E53935] text-white font-bold rounded-lg hover:bg-[#c62828] transition-all"
              >
                <Handshake className="w-5 h-5" />
                {t.cta}
              </a>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, x: 30 }} 
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="relative"
            >
              <div className="bg-[#111111] border border-gray-800 rounded-2xl p-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 bg-[#E53935]/20 rounded-full flex items-center justify-center">
                    <Building2 className="w-8 h-8 text-[#E53935]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">{t.statsTitle}</h3>
                    <p className="text-gray-400">{t.statsSubtitle}</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-black rounded-lg">
                    <span className="text-gray-300">{t.newLeadsPerMonth}</span>
                    <span className="text-[#6ABF4B] font-bold">50+</span>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-black rounded-lg">
                    <span className="text-gray-300">{t.commissionFees}</span>
                    <span className="text-[#6ABF4B] font-bold">$0</span>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-black rounded-lg">
                    <span className="text-gray-300">{t.setupTime}</span>
                    <span className="text-[#6ABF4B] font-bold">5 min</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 md:py-24 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">{t.howItWorksTitle}</h2>
          </motion.div>
          <div className="grid md:grid-cols-4 gap-6">
            {t.howItWorksSteps.map((step, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-[#111111] border border-gray-800 rounded-xl p-6 text-center"
              >
                <div className="w-12 h-12 bg-[#E53935]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-[#E53935] font-bold text-lg">{i + 1}</span>
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{step.title}</h3>
                <p className="text-gray-400 text-sm">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 md:py-24 bg-black">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">{t.benefitsTitle}</h2>
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {t.benefits.map((benefit, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-[#111111] border border-gray-800 rounded-xl p-6"
              >
                <CheckCircle2 className="w-8 h-8 text-[#6ABF4B] mb-4" />
                <h3 className="text-lg font-bold text-white mb-2">{benefit.title}</h3>
                <p className="text-gray-400 text-sm">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 md:py-24 bg-[#0a0a0a]">
        <div className="max-w-3xl mx-auto px-4 md:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">{t.contactTitle}</h2>
            <p className="text-gray-400 mb-8">{t.contactDescription}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href={`mailto:${t.contactEmail}`}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#E53935] text-white font-semibold rounded-lg hover:bg-[#c62828] transition-all"
              >
                <Mail className="w-5 h-5" />
                {t.contactEmail}
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-black border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
          <Link to={`/?lang=${lang}`} className="text-xl font-black">
            <span className="text-[#6ABF4B]">just</span>
            <span className="text-[#E53935]">delivery</span>
          </Link>
          <p className="text-gray-500 text-sm mt-2">
            JD NETWORK PTY LTD (ACN 695 764 071)
          </p>
        </div>
      </footer>
    </div>
  );
}
